import { useState, useRef, useEffect, type ChangeEvent, type FormEvent } from "react";
import { z } from "zod";
import { supabase } from "@/integrations/supabase/client";
import { SiteNav } from "@/components/site/SiteNav";
import { SiteFooter } from "@/components/site/SiteFooter";
import { PageMeta } from "@/components/site/PageMeta";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { toast } from "sonner";
import { CheckCircle2, Heart, ImagePlus, Loader2, ShieldCheck, X } from "lucide-react";
import {
  nameField,
  emailField,
  checkRateLimit,
  evaluateBotSignals,
  formatRetryAfter,
} from "@/lib/form-security";
import { HoneypotFields } from "@/components/site/HoneypotFields";
import { trackEvent } from "@/lib/tracking";
import {
  STORY_IMAGE_ACCEPT_ATTR,
  uploadStoryImage,
  validateStoryImage,
} from "@/lib/story-image";

const storySchema = z.object({
  full_name: nameField,
  email: emailField,
  location: z.string().trim().max(120, "Location too long").optional().or(z.literal("")),
  story_title: z
    .string()
    .trim()
    .min(3, "Give your story a short title")
    .max(200, "Title too long"),
  story_body: z
    .string()
    .trim()
    .min(40, "Please share at least a few sentences (40+ characters)")
    .max(8000, "Story too long — please tighten it under 8,000 characters"),
  consent_publish_story: z.boolean().refine((v) => v === true, {
    message: "We need permission to consider your story for publishing",
  }),
  consent_publish_name: z.boolean(),
  consent_contact: z.boolean(),
});

type Errors = Partial<Record<keyof z.infer<typeof storySchema>, string>>;

export default function ShareStory() {
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [errors, setErrors] = useState<Errors>({});
  const [imageFile, setImageFile] = useState<File | null>(null);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [imageError, setImageError] = useState<string | null>(null);
  const formRef = useRef<HTMLFormElement>(null);
  const imageInputRef = useRef<HTMLInputElement>(null);
  const startedAt = useRef(Date.now());
  const interacted = useRef(false);
  const markInteracted = () => {
    interacted.current = true;
  };

  useEffect(() => {
    if (!imageFile) {
      setImagePreview(null);
      return;
    }
    const url = URL.createObjectURL(imageFile);
    setImagePreview(url);
    return () => URL.revokeObjectURL(url);
  }, [imageFile]);

  const handleImageChange = async (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) {
      setImageFile(null);
      setImageError(null);
      return;
    }
    const result = await validateStoryImage(file);
    if (!result.ok) {
      setImageFile(null);
      setImageError(result.message);
      if (imageInputRef.current) imageInputRef.current.value = "";
      return;
    }
    setImageError(null);
    setImageFile(result.file);
  };

  const clearImage = () => {
    setImageFile(null);
    setImageError(null);
    if (imageInputRef.current) imageInputRef.current.value = "";
  };

  const clearError = (k: keyof Errors) =>
    setErrors((e) => (e[k] ? { ...e, [k]: undefined } : e));

  const handleSubmit = async (e: FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    if (submitting) return;

    const fd = new FormData(e.currentTarget);

    // Layer 1: honeypot decoys, human-interaction check, min fill time.
    const bot = evaluateBotSignals({
      formData: fd,
      startedAt: startedAt.current,
      interacted: interacted.current,
    });
    if (bot.verdict === "drop") {
      trackEvent("story_submit_blocked", { reason: bot.reason });
      // Pretend it worked so bots get no feedback loop.
      setSubmitted(true);
      return;
    }
    if (bot.verdict === "challenge") {
      trackEvent("story_submit_blocked", { reason: bot.reason });
      toast.error(bot.message);
      return;
    }

    // Layer 2: best-effort client rate limit (RLS remains the real backstop).
    const rl = checkRateLimit({ bucket: "share-story", max: 3, windowMs: 60 * 60 * 1000 });
    if (!rl.ok) {
      trackEvent("story_submit_blocked", { reason: "rate-limited" });
      toast.error(
        `You've submitted a few stories already. Please try again in ${formatRetryAfter(rl.retryAfterMs)}.`,
      );
      return;
    }

    const raw = {
      full_name: String(fd.get("full_name") ?? ""),
      email: String(fd.get("email") ?? ""),
      location: String(fd.get("location") ?? ""),
      story_title: String(fd.get("story_title") ?? ""),
      story_body: String(fd.get("story_body") ?? ""),
      consent_publish_story: fd.get("consent_publish_story") === "on",
      consent_publish_name: fd.get("consent_publish_name") === "on",
      consent_contact: fd.get("consent_contact") === "on",
    };

    const parsed = storySchema.safeParse(raw);
    if (!parsed.success) {
      const flat: Errors = {};
      for (const issue of parsed.error.issues) {
        const k = issue.path[0] as keyof Errors;
        if (!flat[k]) flat[k] = issue.message;
      }
      setErrors(flat);
      const firstKey = Object.keys(flat)[0];
      if (firstKey && formRef.current) {
        const el = formRef.current.querySelector<HTMLElement>(`[name="${firstKey}"]`);
        el?.focus();
      }
      return;
    }

    if (imageError) {
      toast.error(imageError);
      return;
    }

    setSubmitting(true);
    try {
      let imagePath: string | null = null;
      if (imageFile) {
        // Re-validate right before upload so a swapped File can't bypass checks.
        const recheck = await validateStoryImage(imageFile);
        if (!recheck.ok) {
          setImageError(recheck.message);
          toast.error(recheck.message);
          setSubmitting(false);
          return;
        }
        imagePath = await uploadStoryImage(recheck.file);
      }

      const { location, ...rest } = parsed.data;
      const { error } = await supabase.from("story_submissions").insert({
        ...rest,
        ...(location ? { location } : {}),
        ...(imagePath ? { image_path: imagePath } : {}),
        user_agent: navigator.userAgent.slice(0, 500),
      });
      if (error) throw error;
      trackEvent("story_submit_success", {
        has_name_consent: parsed.data.consent_publish_name,
        has_image: !!imagePath,
      });
      setSubmitted(true);
      toast.success("Thank you — your story has been received.");
    } catch (err) {
      console.error("[ShareStory] submit failed", err);
      trackEvent("story_submit_error");
      toast.error("Something went wrong. Please try again in a moment.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <PageMeta
        title="Share Your Story — Dr. Sully Speaks"
        description="Tell Dr. Sully how the MAKECHANGE work has shaped your life. Approved stories may be published to encourage others."
        path="/share-your-story"
      />
      <SiteNav />

      <main className="container mx-auto max-w-3xl px-4 py-16">
        <header className="mb-10 text-center">
          <div className="inline-flex items-center gap-2 rounded-full border border-primary/30 bg-primary/5 px-4 py-1.5 text-sm font-medium text-primary">
            <Heart className="h-4 w-4" aria-hidden />
            Your story matters
          </div>
          <h1 className="mt-4 font-serif text-4xl font-bold tracking-tight md:text-5xl">
            Share Your Story
          </h1>
          <p className="mx-auto mt-4 max-w-2xl text-lg text-muted-foreground">
            Tell us how the MAKECHANGE framework, a book, a session, or a moment shifted
            something for you. Approved stories may be featured to encourage others walking
            a similar road.
          </p>
        </header>

        {submitted ? (
          <section
            role="status"
            aria-live="polite"
            className="rounded-2xl border border-primary/30 bg-card p-10 text-center shadow-lg"
          >
            <CheckCircle2 className="mx-auto h-14 w-14 text-primary" aria-hidden />
            <h2 className="mt-4 font-serif text-3xl font-bold">Thank you.</h2>
            <p className="mx-auto mt-4 max-w-xl text-muted-foreground">
              Your story is now with our team. Every submission is read personally. If it's
              selected for publishing, we'll reach out first — you always control how (or
              whether) your name appears.
            </p>
            <div className="mt-8 grid gap-4 text-left sm:grid-cols-3">
              {[
                { n: "1", t: "Received", d: "Your story is safely logged." },
                { n: "2", t: "Reviewed", d: "Our team reads it within a few weeks." },
                { n: "3", t: "Published", d: "If selected, we contact you before anything goes live." },
              ].map((s) => (
                <div key={s.n} className="rounded-xl border bg-background/50 p-4">
                  <div className="text-2xl font-bold text-primary">{s.n}</div>
                  <div className="mt-1 font-semibold">{s.t}</div>
                  <div className="text-sm text-muted-foreground">{s.d}</div>
                </div>
              ))}
            </div>
            <div className="mt-8 flex flex-wrap items-center justify-center gap-3">
              <Button asChild variant="outline">
                <a href="/">Back to homepage</a>
              </Button>
              <Button asChild>
                <a href="/masterclass">Explore the Masterclass</a>
              </Button>
            </div>
          </section>
        ) : (
          <form
            ref={formRef}
            onSubmit={handleSubmit}
            noValidate
            onPointerDown={markInteracted}
            onKeyDown={markInteracted}
            className="relative space-y-6 rounded-2xl border bg-card p-6 shadow-sm md:p-10"
          >
            <HoneypotFields />

            <div className="grid gap-4 md:grid-cols-2">
              <div>
                <Label htmlFor="full_name">Your name *</Label>
                <Input
                  id="full_name"
                  name="full_name"
                  required
                  aria-invalid={!!errors.full_name}
                  onBlur={() => clearError("full_name")}
                  className={errors.full_name ? "border-destructive" : ""}
                />
                {errors.full_name && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors.full_name}
                  </p>
                )}
              </div>
              <div>
                <Label htmlFor="email">Email *</Label>
                <Input
                  id="email"
                  name="email"
                  type="email"
                  required
                  aria-invalid={!!errors.email}
                  onBlur={() => clearError("email")}
                  className={errors.email ? "border-destructive" : ""}
                />
                {errors.email && (
                  <p role="alert" className="mt-1 text-sm text-destructive">
                    {errors.email}
                  </p>
                )}
              </div>
            </div>

            <div>
              <Label htmlFor="location">City / region (optional)</Label>
              <Input
                id="location"
                name="location"
                placeholder="San Antonio, TX"
                onBlur={() => clearError("location")}
              />
            </div>

            <div>
              <Label htmlFor="story_title">Give your story a title *</Label>
              <Input
                id="story_title"
                name="story_title"
                required
                maxLength={200}
                placeholder="e.g. Rebuilding after the storm"
                aria-invalid={!!errors.story_title}
                onBlur={() => clearError("story_title")}
                className={errors.story_title ? "border-destructive" : ""}
              />
              {errors.story_title && (
                <p role="alert" className="mt-1 text-sm text-destructive">
                  {errors.story_title}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="story_body">Your story *</Label>
              <Textarea
                id="story_body"
                name="story_body"
                required
                rows={10}
                maxLength={8000}
                placeholder="Share what changed — the moment, the shift, the outcome. Take your time."
                aria-invalid={!!errors.story_body}
                onBlur={() => clearError("story_body")}
                className={errors.story_body ? "border-destructive" : ""}
              />
              <p className="mt-1 text-xs text-muted-foreground">
                40–8,000 characters. Plain text only.
              </p>
              {errors.story_body && (
                <p role="alert" className="mt-1 text-sm text-destructive">
                  {errors.story_body}
                </p>
              )}
            </div>

            <div>
              <Label htmlFor="story_image">Add a photo (optional)</Label>
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <input
                  ref={imageInputRef}
                  id="story_image"
                  name="story_image"
                  type="file"
                  accept={STORY_IMAGE_ACCEPT_ATTR}
                  onChange={(e) => void handleImageChange(e)}
                  aria-describedby="story_image_help"
                  aria-invalid={!!imageError}
                  className="block w-full text-sm text-muted-foreground file:mr-3 file:rounded-md file:border-0 file:bg-primary/10 file:px-4 file:py-2 file:text-sm file:font-medium file:text-primary hover:file:bg-primary/20 sm:w-auto"
                />
                {imageFile && (
                  <Button type="button" variant="ghost" size="sm" onClick={clearImage}>
                    <X className="mr-1 h-4 w-4" aria-hidden />
                    Remove
                  </Button>
                )}
              </div>
              <p id="story_image_help" className="mt-1 flex items-start gap-1.5 text-xs text-muted-foreground">
                <ImagePlus className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden />
                JPG, PNG, or WebP up to 5&nbsp;MB. Your photo stays private and is only shown publicly if a
                moderator approves and publishes your story.
              </p>
              {imageError && (
                <p role="alert" className="mt-1 text-sm text-destructive">
                  {imageError}
                </p>
              )}
              {imagePreview && (
                <img
                  src={imagePreview}
                  alt="Preview of the photo you selected"
                  className="mt-3 max-h-48 rounded-lg border object-contain"
                />
              )}
            </div>



            <fieldset className="space-y-3 rounded-xl border bg-background/60 p-5">
              <legend className="flex items-center gap-2 px-1 text-sm font-semibold">
                <ShieldCheck className="h-4 w-4 text-primary" aria-hidden />
                Consent
              </legend>

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_publish_story" name="consent_publish_story" />
                <span>
                  <strong>Required.</strong> I grant Dr. Sully Speaks permission to review
                  my story and, if selected, publish it (in whole or edited for length /
                  clarity) on drsullyspeaks.com and related channels.
                </span>
              </label>
              {errors.consent_publish_story && (
                <p role="alert" className="text-sm text-destructive">
                  {errors.consent_publish_story}
                </p>
              )}

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_publish_name" name="consent_publish_name" />
                <span>
                  You may publish my <strong>first name and last initial</strong> alongside
                  my story. (If unchecked, your story will be published anonymously.)
                </span>
              </label>

              <label className="flex items-start gap-3 text-sm">
                <Checkbox id="consent_contact" name="consent_contact" />
                <span>
                  You may contact me by email about my submission (edits, clarifications,
                  or publication notice).
                </span>
              </label>
            </fieldset>

            <Button
              type="submit"
              size="lg"
              disabled={submitting}
              className="w-full sm:w-auto"
            >
              {submitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" aria-hidden />
                  Submitting…
                </>
              ) : (
                "Share my story"
              )}
            </Button>

            <p className="text-xs text-muted-foreground">
              Submissions are moderated. We never sell your data. See our{" "}
              <a href="/legal" className="underline">
                privacy notice
              </a>
              .
            </p>
          </form>
        )}
      </main>

      <SiteFooter />
    </div>
  );
}
